import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Trash2, ChevronDown, ChevronUp, Mail, Phone, MapPin, User } from "lucide-react";

const STATUS_META = {
  nuova: { label: "Nuova", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  contattato: { label: "Contattato", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  preventivo_inviato: { label: "Preventivo inviato", cls: "bg-purple-100 text-purple-700 border-purple-300" },
  attivato: { label: "Attivato", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  chiusa: { label: "Chiusa", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};

const GROUP_LABELS = {
  negozi: "Negozi e vendita",
  alimentari: "Alimentari e vendita al banco",
  ristorazione: "Bar e ristorazione",
  servizi: "Servizi e appuntamenti",
  officine: "Laboratori, officine e auto",
  eventi: "Eventi e ospitalità",
  ospitalita: "Alberghiero e ospitalità",
};

const SERVE_LABELS = { banco: "Solo banco / asporto", tavoli: "Anche ai tavoli", banco_tavoli: "Banco + tavoli" };

export default function AdminQuoteRequests() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);

  const load = () => api.get(`/admin/quote-requests${filter ? `?status=${filter}` : ""}`).then((r) => setItems(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quote-requests/${id}`, { status });
      toast.success("Stato aggiornato");
      load();
    } catch { toast.error("Errore"); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try {
      await api.delete(`/admin/quote-requests/${id}`);
      toast.success("Richiesta eliminata");
      load();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Commerciale</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2 flex items-center gap-3">
        <Inbox className="w-8 h-8" /> Richieste di preventivo
      </h1>
      <p className="text-velta-mute mb-8">Le richieste inviate dal wizard pubblico. Contatta il cliente, poi prepara configurazione e preventivo da "Clienti".</p>

      <div className="flex flex-wrap gap-2 mb-6">
        <button data-testid="qr-filter-all" onClick={() => setFilter("")} className={`text-xs px-3 h-9 rounded-md border-2 font-medium ${!filter ? "border-velta-ink bg-velta-ink text-white" : "border-velta-border bg-white"}`}>Tutte</button>
        {Object.entries(STATUS_META).map(([k, v]) => (
          <button key={k} data-testid={`qr-filter-${k}`} onClick={() => setFilter(k)} className={`text-xs px-3 h-9 rounded-md border-2 font-medium ${filter === k ? "border-velta-ink bg-velta-ink text-white" : "border-velta-border bg-white"}`}>{v.label}</button>
        ))}
      </div>

      {items.length === 0 && <p className="text-sm text-velta-mute" data-testid="qr-empty">Nessuna richiesta{filter ? " con questo stato" : " al momento"}.</p>}

      <div className="space-y-3">
        {(Array.isArray(items) ? items : []).map((r) => {
          const st = STATUS_META[r.status] || STATUS_META.nuova;
          const isOpen = open === r.id;
          return (
            <div key={r.id} data-testid={`qr-row-${r.id}`} className="bg-white border border-velta-border rounded-md">
              <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setOpen(isOpen ? null : r.id)}>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold flex items-center gap-2 flex-wrap">
                    {r.business_name}
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${st.cls}`}>{st.label}</span>
                    {r.answers?.servizio_clienti && (
                      <span data-testid={`qr-serve-${r.id}`} className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border bg-velta-bg border-velta-border text-velta-ink">
                        {SERVE_LABELS[r.answers.servizio_clienti] || r.answers.servizio_clienti}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-velta-mute mt-0.5">
                    {GROUP_LABELS[r.group] || r.group} → <strong className="text-velta-ink">{r.activity_label || r.activity_id}</strong> · {formatDate(r.created_at)}
                  </div>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-velta-mute" /> : <ChevronDown className="w-4 h-4 text-velta-mute" />}
              </div>
              {isOpen && (
                <div className="border-t border-velta-border p-4 space-y-4" data-testid={`qr-detail-${r.id}`}>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="space-y-1.5">
                      {r.contact_name && <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-velta-mute" /> {r.contact_name}</div>}
                      {r.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-velta-mute" /> <a href={`mailto:${r.email}`} className="underline">{r.email}</a></div>}
                      {r.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-velta-mute" /> <a href={`tel:${r.phone}`} className="underline">{r.phone}</a></div>}
                      {r.city && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-velta-mute" /> {r.city}</div>}
                    </div>
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1.5">Esigenze dichiarate</div>
                      {(r.answers_readable || []).length === 0 && <div className="text-xs text-velta-mute">Nessuna esigenza specificata.</div>}
                      <ul className="space-y-1">
                        {(Array.isArray(r.answers_readable) ? r.answers_readable : []).map((a, i) => (
                          <li key={i} className="text-xs flex gap-1.5">
                            <span className="text-[#2F9E77] font-bold">✓</span>
                            <span>{a.label}{a.value !== "Sì" ? `: ${a.value}` : ""}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  {r.notes && (
                    <div className="bg-velta-bg rounded p-3 text-xs italic">"{r.notes}"</div>
                  )}
                  <div className="flex items-center gap-2 pt-2 border-t border-velta-border/60">
                    <select
                      data-testid={`qr-status-${r.id}`}
                      value={r.status}
                      onChange={(e) => setStatus(r.id, e.target.value)}
                      className="h-9 px-2 border border-velta-border rounded-md text-xs bg-white outline-none focus:border-velta-ink"
                    >
                      {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <button
                      data-testid={`qr-goto-clients-${r.id}`}
                      onClick={() => navigate("/admin/clienti", { state: { fromRequest: r } })}
                      className="h-9 px-3 inline-flex items-center border-2 border-velta-ink rounded-md text-xs font-semibold hover:bg-velta-ink hover:text-white transition"
                    >
                      Prepara configurazione →
                    </button>
                    <button data-testid={`qr-del-${r.id}`} onClick={() => del(r.id)} className="ml-auto h-9 w-9 rounded hover:bg-red-50 flex items-center justify-center text-velta-error">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
